import { HTTPError } from "@marketplace/http";
import { notifications } from "@marketplace/ui/notifications";
import {
  useMutation,
  type UseMutationOptions,
  useQueryClient
} from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { SetPublishStatusRequestDto } from "../../dtos/set-publish-status-request-dto";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { ProductService } from "../../services/product-service";

type UsePutSetPublishStatusMutationParams = {
  options?: UseMutationOptions<
    { success: boolean },
    unknown,
    SetPublishStatusRequestDto
  >;
};

export const usePutSetPublishStatusMutation = ({
  options
}: UsePutSetPublishStatusMutationParams = {}) => {
  const queryClient = useQueryClient();
  const { q, page, sort, draft_page, draft_q, draft_sort } = useSearch({
    from: "/products/list/"
  });
  const { t } = useTranslation("product");

  return useMutation({
    mutationFn: async (payload) => {
      return await ProductService.setPublishStatus({ payload });
    },
    onSuccess: async (...args) => {
      const [, variable] = args;
      // Invalidate product list
      const currentPageKey = ProductQueryKeys.getProductList({
        limit: 10,
        page,
        q,
        sort
      }).queryKey;

      await queryClient.invalidateQueries({ queryKey: currentPageKey });

      // Invalidate draft list
      const draftListKey = ProductQueryKeys.getProductDraftList({
        limit: 10,
        page: draft_page,
        q: draft_q,
        sort: draft_sort
      }).queryKey;

      await queryClient.invalidateQueries({ queryKey: draftListKey });

      const messageKey = variable.is_publish
        ? "notifications.successPublishProduct"
        : "notifications.successUnpublishProduct";

      notifications.show({
        message: t(messageKey)
      });

      options?.onSuccess?.(...args);
    },
    onError: (...errorArgs) => {
      const [error, variable] = errorArgs;
      if (error instanceof HTTPError) {
        const messageKey = variable.is_publish
          ? "notifications.failedToPublishProduct"
          : "notifications.failedToUnpublishProduct";

        notifications.show({
          variant: "error",
          message: t(messageKey)
        });
      }

      options?.onError?.(...errorArgs);
    },
    ...options
  });
};
